import { FeatureTypeEnum } from "@/types/enum";
import { RoofType } from "@/types/building";
import type { GeoJSONFeature } from "mapbox-gl";
import {
  BoxGeometry,
  BufferGeometry,
  Color,
  CylinderGeometry,
  Group,
  Matrix4,
  Mesh,
  MeshLambertMaterial,
  Object3D,
} from "three";
import { degToRad } from "three/src/math/MathUtils.js";
import {
  createDormerRoof,
  createFlatRoof,
  createGableRoof,
  createHipRoof,
  createMansardRoof,
  createRoundRoof,
  createShedRoof,
} from "@/utils/building-utils";
import {
  featuresToBufferGeometry,
  getFeatureHeight,
  lngLatToLocalMeters,
  mergePositionGeometries,
  normalizeGeometryPositionsOnly,
  type LocalOrigin,
} from "./geometryUtils";

function isUserBuildingFeature(feature: GeoJSONFeature): boolean {
  const category = feature.properties?.category;
  if (
    category === FeatureTypeEnum.BUILDING ||
    category === "building" ||
    feature.properties?.roofType
  ) {
    return Boolean(feature.properties?.width && feature.properties?.length);
  }
  return false;
}

function isUserPolygonFeature(feature: GeoJSONFeature): boolean {
  if (isUserBuildingFeature(feature)) return false;

  const category = feature.properties?.category;
  if (category === FeatureTypeEnum.POLYGON || category === "polygon") {
    return getFeatureHeight(feature) > 0;
  }

  return (
    feature.geometry?.type === "Polygon" &&
    Boolean(feature.properties?.contentId) &&
    getFeatureHeight(feature) > 0
  );
}

function isContextBuildingFeature(feature: GeoJSONFeature): boolean {
  return (
    !isUserBuildingFeature(feature) &&
    !isUserPolygonFeature(feature) &&
    getFeatureHeight(feature) > 0
  );
}

function getFeatureCenterLngLat(
  feature: GeoJSONFeature,
): [number, number] | null {
  const geometry = feature.geometry;
  if (!geometry) return null;

  if (geometry.type === "Point") {
    return geometry.coordinates as [number, number];
  }

  if (geometry.type === "Polygon") {
    const ring = geometry.coordinates[0] as number[][];
    if (!ring?.length) return null;
    const count = ring.length > 1 ? ring.length - 1 : ring.length;
    let lng = 0;
    let lat = 0;
    for (let i = 0; i < count; i++) {
      lng += ring[i][0];
      lat += ring[i][1];
    }
    return [lng / count, lat / count];
  }

  return null;
}

function buildBuildingGroup(
  width: number,
  length: number,
  height: number,
  roofType: RoofType,
  roofAngleDeg: number,
): Group {
  const group = new Group();
  const roofColor = new Color("#8b5cf6");
  const extrudeColor = new Color("white");
  const dimensions = { width, length, height, roofAngleDeg };

  let bodyGeometry: BufferGeometry;
  if (roofType === RoofType.ROUND) {
    bodyGeometry = new CylinderGeometry(width / 2, width / 2, height, 32);
  } else {
    bodyGeometry = new BoxGeometry(width, height, length);
  }

  const body = new Mesh(
    bodyGeometry,
    new MeshLambertMaterial({ color: "white" }),
  );
  body.position.y = height / 2;
  group.add(body);

  switch (roofType) {
    case RoofType.FLAT:
      group.add(createFlatRoof(dimensions, roofColor));
      break;
    case RoofType.GABLE:
      group.add(createGableRoof(dimensions, roofColor, extrudeColor));
      break;
    case RoofType.HIP:
      group.add(createHipRoof(dimensions, roofColor));
      break;
    case RoofType.SHED:
      group.add(createShedRoof(dimensions, roofColor, extrudeColor));
      break;
    case RoofType.DORMER:
      group.add(createDormerRoof(dimensions, roofColor));
      break;
    case RoofType.MANSARD:
      group.add(createMansardRoof(dimensions, roofColor, extrudeColor));
      break;
    case RoofType.ROUND:
      group.add(createRoundRoof(dimensions, roofColor));
      break;
    default:
      break;
  }

  return group;
}

function collectTransformedGeometries(
  object: Object3D,
  transform: Matrix4,
): BufferGeometry[] {
  const geometries: BufferGeometry[] = [];
  object.updateMatrixWorld(true);

  object.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const geometry = child.geometry.clone();
    const worldMatrix = transform.clone().multiply(child.matrixWorld);
    geometry.applyMatrix4(worldMatrix);
    geometries.push(normalizeGeometryPositionsOnly(geometry));
    geometry.dispose();
  });

  return geometries;
}

function disposeObject3D(object: Object3D) {
  object.traverse((child) => {
    if (child instanceof Mesh) {
      child.geometry.dispose();
      if (Array.isArray(child.material)) {
        child.material.forEach((material) => material.dispose());
      } else {
        child.material.dispose();
      }
    }
  });
}

export function buildingFeatureToBufferGeometry(
  feature: GeoJSONFeature,
  origin: LocalOrigin,
): BufferGeometry | null {
  const props = feature.properties ?? {};
  const width = Number(props.width) || 10;
  const length = Number(props.length) || 10;
  const height = Number(props.height) || 5;
  const roofType = (props.roofType as RoofType) || RoofType.FLAT;
  const roofAngleDeg = Number(props.roofAngleDeg) || 20;
  const rotation = Number(props.rotation) || 0;
  const rotationRad =
    Math.abs(rotation) > Math.PI * 2 ? degToRad(rotation) : rotation;

  const center =
    feature.geometry?.type === "Point"
      ? (feature.geometry.coordinates as [number, number])
      : getFeatureCenterLngLat(feature);
  if (!center) return null;

  const local = lngLatToLocalMeters(center[0], center[1], origin);
  const group = buildBuildingGroup(
    width,
    length,
    height,
    roofType,
    roofAngleDeg,
  );

  // Same orientation as MapboxBuildingObject custom layer: Ry(bearing) then Rx(90°).
  const transform = new Matrix4()
    .makeTranslation(local.x, local.y, 0)
    .multiply(new Matrix4().makeRotationX(Math.PI / 2))
    .multiply(new Matrix4().makeRotationY(rotationRad));

  const geometries = collectTransformedGeometries(group, transform);
  disposeObject3D(group);

  if (geometries.length === 0) return null;
  const merged = mergePositionGeometries(geometries);
  geometries.forEach((geometry) => geometry.dispose());
  return merged;
}

export function buildSimulationGeometryFromFeatures(
  features: GeoJSONFeature[],
  origin: LocalOrigin,
): {
  simulation: BufferGeometry | null;
  contextShading: BufferGeometry | null;
} {
  const polygonFeatures: GeoJSONFeature[] = [];
  const buildingFeatures: GeoJSONFeature[] = [];
  const contextFeatures: GeoJSONFeature[] = [];

  for (const feature of features) {
    if (isUserBuildingFeature(feature)) {
      buildingFeatures.push(feature);
    } else if (isUserPolygonFeature(feature)) {
      polygonFeatures.push(feature);
    } else if (isContextBuildingFeature(feature)) {
      contextFeatures.push(feature);
    }
  }

  const geometries: BufferGeometry[] = [];

  const polygonGeometry = featuresToBufferGeometry(polygonFeatures, origin);
  if (polygonGeometry) geometries.push(polygonGeometry);

  for (const buildingFeature of buildingFeatures) {
    const buildingGeometry = buildingFeatureToBufferGeometry(
      buildingFeature,
      origin,
    );
    if (buildingGeometry) {
      geometries.push(buildingGeometry);
      continue;
    }

    // Fallback when width/length/roof props are missing but a footprint exists.
    const footprintGeometry = featuresToBufferGeometry(
      [buildingFeature],
      origin,
    );
    if (footprintGeometry) geometries.push(footprintGeometry);
  }

  let simulationData: BufferGeometry | null = null;
  if (geometries.length > 0) {
    simulationData = mergePositionGeometries(geometries);
    geometries.forEach((geometry) => geometry.dispose());
  }

  const contextShading = featuresToBufferGeometry(contextFeatures, origin);

  return { simulation: simulationData, contextShading };
}

/** Simulate only target features; use all other geometry as context shading. */
export function buildSimulationGeometryForTarget(
  simulationFeatures: GeoJSONFeature[],
  shadingFeatures: GeoJSONFeature[],
  origin: LocalOrigin,
): {
  simulation: BufferGeometry | null;
  contextShading: BufferGeometry | null;
} {
  const target = buildSimulationGeometryFromFeatures(simulationFeatures, origin);
  const surroundings = buildSimulationGeometryFromFeatures(
    shadingFeatures,
    origin,
  );

  const contextParts: BufferGeometry[] = [];
  if (surroundings.simulation) contextParts.push(surroundings.simulation);
  if (surroundings.contextShading) contextParts.push(surroundings.contextShading);
  if (target.contextShading) contextParts.push(target.contextShading);

  let contextShading: BufferGeometry | null = null;
  if (contextParts.length > 0) {
    contextShading = mergePositionGeometries(contextParts);
    contextParts.forEach((geometry) => geometry.dispose());
  }

  surroundings.simulation?.dispose();

  return {
    simulation: target.simulation,
    contextShading,
  };
}
